import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import countries from 'i18n-iso-countries';
import 'i18n-iso-countries/langs/en.json';
import { TbChevronDown, TbChevronUp } from 'react-icons/tb';

import AgentBalanceList from './AgentBalanceList';
import BalanceAdjustmentModal from './BalanceAdjustmentModal';
import { AgentBalancesApi, UsersApi } from '../../../api/common/apiIndex';
import useDebounce from '../hooks/useDebounce';
import Filters from '../businessUtilities/Filters';
import './CountryBalance.css';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { useIsDarkTheme } from '../hooks/useIsDarkTheme';
import Loader from '../../common/commonUtils/Loader';
import { toast } from 'react-toastify';

countries.registerLocale(require('i18n-iso-countries/langs/en.json'));

const EMPTY = Object.freeze([]);
const normalizeId = (id) => {
  if (id === undefined || id === null) return '';
  return String(id);
};

const Balances = () => {
  const [expanded, setExpanded] = useState(() => new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [adjustmentAction, setAdjustmentAction] = useState(null);
  const [selectionKey, setSelectionKey] = useState('');
  const [adjustmentForm, setAdjustmentForm] = useState({ amount: '', reason: '' });
  const [adjustmentError, setAdjustmentError] = useState('');

  const q = useDebounce(searchTerm, 300);
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const isDark = useIsDarkTheme();
  const queryClient = useQueryClient();
  const nameToCode = (name) =>
    countries.getAlpha2Code(String(name || ''), 'en')?.toUpperCase() || '';

  const selectedCode = useMemo(() => nameToCode(selectedCountry), [selectedCountry]);

  const {
    data: page,
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: ['agent-balance-snapshots', { q, country: selectedCode, page: 0, size: 2000 }],
    queryFn: () =>
      AgentBalancesApi.list({
        q: q || undefined,
        country: selectedCode || undefined,
        page: 0,
        size: 2000,
        sort: 'agentId,asc',
      }),
    keepPreviousData: true,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });

  const { data: activeAgents = [] } = useQuery({
    queryKey: ['agents-all', { page: 0, size: 1000, sort: 'firstName,asc', activeOnly: true }],
    queryFn: async () => {
      const res = await UsersApi.listAgents({
        page: 0,
        size: 1000,
        sort: 'firstName,asc',
        activeOnly: true,
      });
      if (Array.isArray(res)) return res;
      if (Array.isArray(res?.content)) return res.content;
      return [];
    },
    staleTime: 10 * 60 * 1000,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const activeAgentIds = useMemo(() => {
    const set = new Set();
    activeAgents.forEach((agent) => {
      const idStr = normalizeId(agent?.id);
      if (idStr) set.add(idStr);
    });
    return set;
  }, [activeAgents]);

  const rows = page?.content ?? [];

  const snapshots = useMemo(() => {
    if (!page?.content) return EMPTY;
    return Array.isArray(page.content) ? page.content : EMPTY;
  }, [page]);

  const getCountry = (row) => {
    const code = String(row?.country || '').toUpperCase();
    return countries.getName(code, 'en') || '';
  };

  const countriesViewBase = useMemo(() => {
    const by = new Map();

    for (const s of snapshots) {
      const code = String(s.country || '').toUpperCase();
      if (!code) continue;

      const name = countries.getName(code, 'en') || code;
      const entry =
        by.get(code) ||
        {
          code,
          name,
          agents: [],
          totalsMap: new Map(),
          totalAvailableAll: 0,
          currency: s.currency || '',
        };

      const cur = (s.currency || '').toUpperCase();
      const available = Number(s.available || 0);
      const reserved = Number(s.reserved || 0);

      const key = `${s.agentId}-${cur}-${code}`;
      entry.agents.push({
        id: s.agentId,
        name: s.agentName || `Agent #${s.agentId}`,
        uuid: s.agentUuid,
        available,
        reserved,
        currency: cur,
        countryCode: code,
        countryName: name,
        key,
      });

      entry.totalsMap.set(cur, (entry.totalsMap.get(cur) || 0) + available);
      entry.totalAvailableAll += available;

      by.set(code, entry);
    }

    return Array.from(by.values()).map((c) => ({
      ...c,
      totalsByCurrency: Array.from(c.totalsMap.entries()),
      agentsCount: c.agents.length,
      currency: c.currency,
    }));
  }, [snapshots]);

  const countriesView = useMemo(() => {
    const copy = [...countriesViewBase];
    copy.sort((a, b) => a.name.localeCompare(b.name));
    return copy;
  }, [countriesViewBase]);

  const adjustableAgents = useMemo(() => {
    const map = new Map();
    countriesView.forEach((country) => {
      const list = country.agents || EMPTY;
      list.forEach((agent) => {
        const idStr = normalizeId(agent?.id);
        if (!idStr || !activeAgentIds.has(idStr)) return;
        const key = agent.key || `${agent.id}-${agent.currency}-${agent.countryCode || country.code}`;
        if (!map.has(key)) {
          map.set(key, {
            ...agent,
            key,
            countryCode: agent.countryCode || country.code,
            countryName: agent.countryName || country.name,
          });
        }
      });
    });
    return Array.from(map.values());
  }, [activeAgentIds, countriesView]);

  const selectedAdjustmentAgent = useMemo(
    () => adjustableAgents.find((agent) => agent.key === selectionKey) || null,
    [adjustableAgents, selectionKey]
  );

  useEffect(() => {
    if (!selectionKey) return;
    const exists = adjustableAgents.some((agent) => agent.key === selectionKey);
    if (!exists) setSelectionKey('');
  }, [adjustableAgents, selectionKey]);

  const totalCountries = countriesView.length;

  const totalAgents = useMemo(
    () => countriesView.reduce((sum, c) => sum + c.agentsCount, 0),
    [countriesView]
  );

  const showLoading = isLoading || (isFetching && !page);

  const expandAll = () => {
    const set = new Set(countriesView.map((c) => c.code));
    setExpanded(set);
  };

  const collapseAll = () => setExpanded(new Set());

  const toggleExpand = (code) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCountry('');
  };

  useEffect(() => {
    setExpanded(prev => {
      if (prev.size === 0) return prev;
      const valid = new Set(countriesView.map(c => c.code));
      const next = new Set([...prev].filter(code => valid.has(code)));
      return next.size === prev.size ? prev : next;
    });
  }, [countriesView]);

  const openAdjustmentModal = (action, agent) => {
    if (agent) {
      const idStr = normalizeId(agent.id);
      if (!idStr || !activeAgentIds.has(idStr)) return;
    }
    const key =
      agent?.key ||
      (agent
        ? `${agent.id}-${agent.currency}-${agent.countryCode || agent.country || ''}`
        : '');
    setAdjustmentAction(action);
    setSelectionKey(key);
    setAdjustmentForm({ amount: '', reason: '' });
    setAdjustmentError('');
  };

  const closeAdjustmentModal = () => {
    if (adjustmentMutation.isPending) return;
    setAdjustmentAction(null);
    setSelectionKey('');
    setAdjustmentForm({ amount: '', reason: '' });
    setAdjustmentError('');
  };

  const handleSelectionChange = (value) => {
    setSelectionKey(value);
    if (adjustmentError) setAdjustmentError('');
  };

  const handleAdjustmentChange = (field, value) => {
    setAdjustmentForm((prev) => ({ ...prev, [field]: value }));
    if (adjustmentError) setAdjustmentError('');
  };

  const handleAdjustmentSubmit = (event) => {
    event.preventDefault();
    if (adjustmentMutation.isPending) return;
    if (!adjustmentAction) {
      setAdjustmentError('Select an action to continue.');
      return;
    }
    const agent = selectedAdjustmentAgent;
    if (!agent) {
      setAdjustmentError('Select an agent balance to continue.');
      return;
    }
    const amount = Number(adjustmentForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setAdjustmentError('Enter a valid amount greater than zero.');
      return;
    }
    const reason = adjustmentForm.reason.trim();
    if (!reason) {
      setAdjustmentError('Provide a reason for this adjustment.');
      return;
    }
    adjustmentMutation.mutate({
      action: adjustmentAction,
      payload: {
        agentId: agent.id,
        country: agent.countryCode,
        currency: agent.currency,
        amount,
        reason,
      },
    });
  };

  const adjustmentMutation = useMutation({
    mutationFn: ({ action, payload }) => {
      if (action === 'refund') return AgentBalancesApi.refund(payload);
      if (action === 'return') return AgentBalancesApi.agentReturn(payload);
      throw new Error('Unsupported balance adjustment action.');
    },
    onSuccess: (_data, variables) => {
      const label = variables?.action === 'return' ? 'Agent return recorded.' : 'Refund submitted successfully.';
      toast.success(label);
      queryClient.invalidateQueries({ queryKey: ['agent-balance-snapshots'] });
      setAdjustmentAction(null);
      setSelectionKey('');
      setAdjustmentForm({ amount: '', reason: '' });
      setAdjustmentError('');
    },
    onError: (err) => {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to submit balance adjustment.';
      setAdjustmentError(msg);
      toast.error(msg);
    },
  });

  const goToAddBalance = () => navigate('/home/add-balance');
  const hasItems = countriesView.length > 0;
  const hasExpanded = expanded.size > 0;
  const allExpanded = hasItems && expanded.size === countriesView.length;
  const modalTitle =
    adjustmentAction === 'return' ? 'Agent Return' : 'Refund Balance';
  const modalSubtitle =
    adjustmentAction === 'return'
      ? 'Record float sent back from an agent.'
      : 'Issue a refund to move funds out of an agent balance.';
  const modalSubmitLabel =
    adjustmentAction === 'return' ? 'Record return' : 'Submit refund';

  return (
    <>
      <div
        className="w-100 d-flex flex-column gap-3"
        style={{ paddingBottom: "calc(var(--app-safe-area-bottom) + 2.5rem)" }}
      >
      <div className="card border shadow rounded-4">
        <div className="card-body">
          <div className="row g-3 align-items-stretch">
            <div className="col-12 col-md-6">
              <div className="h-100 p-3 p-md-4 rounded-4 border bg-body-tertiary">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="d-flex align-items-center gap-2">
                    <i className="bi bi-globe2 fs-5" aria-hidden />
                    <h6 className="mb-0">Countries</h6>
                  </div>
                  <span className="badge rounded-pill bg-body border text-body">
                    <strong>{totalCountries}</strong>
                  </span>
                </div>
                <p className="text-body-secondary small mb-0">
                  Number of countries
                </p>
              </div>
            </div>

            <div className="col-12 col-md-6">
              <div className="h-100 p-3 p-md-4 rounded-4 border bg-body-tertiary">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="d-flex align-items-center gap-2">
                    <i className="bi bi-people fs-5" aria-hidden />
                    <h6 className="mb-0">Agents</h6>
                  </div>
                  <span className="badge rounded-pill bg-body border text-body">
                    <strong>{totalAgents}</strong>
                  </span>
                </div>
                <p className="text-body-secondary small mb-0">
                  Agents across all countries.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card border shadow rounded-4">
        <div className="card-body">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <button
                className={`btn btn-outline-secondary btn-sm rounded-pill d-inline-flex align-items-center gap-2 ${isDark ? 'text-white' : ''}`}
                onClick={() => setShowFilters((s) => !s)}
                aria-expanded={showFilters}
                aria-controls="balance-filters"
                disabled={isFetching}
              >
                <i className="bi bi-funnel" />
                {showFilters ? 'Hide filters' : 'Show filters'}
              </button>

              <div className="btn-group btn-group-sm" role="group" aria-label="Expand or collapse all">
                <button
                  className={`btn btn-outline-secondary rounded-pill d-inline-flex align-items-center gap-2 ${isDark ? 'text-white' : ''}`}
                  onClick={allExpanded ? collapseAll : expandAll}
                  disabled={allExpanded ? !hasExpanded : !hasItems}
                  title={allExpanded ? "Collapse all countries" : "Expand all countries"}
                  aria-pressed={allExpanded}
                >
                  <i className={`bi ${allExpanded ? "bi-arrows-collapse" : "bi-arrows-expand"}`} />
                  {allExpanded ? "Collapse all" : "Expand all"}
                </button>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2">
              {isAdmin && (
                <div className="d-flex flex-wrap gap-2">
                  <button
                    className={`btn btn-outline-secondary btn-sm rounded-pill ${isDark ? 'text-white' : ''}`}
                    onClick={goToAddBalance}
                    title="Add balance"
                  >
                    <i className="bi bi-plus-circle me-1" aria-hidden="true" /> Add Balance
                  </button>
                </div>
              )}
            </div>
          </div>

          {(selectedCountry || searchTerm) && (
            <div className="mt-2 d-flex align-items-center gap-2 flex-wrap">
              <small className="text-body-secondary me-1">Filters:</small>
              {selectedCountry && (
                <span className="badge bg-body-tertiary border text-body">Country: {selectedCountry}</span>
              )}
              {searchTerm && (
                <span className="badge bg-body-tertiary border text-body">Search: “{searchTerm}”</span>
              )}
              <button className="btn btn-sm btn-link text-decoration-none" onClick={clearFilters}>
                Clear filters
              </button>
            </div>
          )}

          {showFilters && (
            <div className="mt-3" id="balance-filters">
              <Filters
                data={rows}
                getCountry={getCountry}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                selectedCountry={selectedCountry}
                onCountryChange={isAdmin ? setSelectedCountry : () => { }}
              />
            </div>
          )}
        </div>
      </div>

      {error && <div className="alert alert-danger m-0">Failed to load balances.</div>}
      {!error && (isLoading || (isFetching && !page)) && <div className="text-body-secondary"><Loader text="Loading…" /></div>}

      {!error && !showLoading && (
        countriesView.length === 0 ? (
          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body text-center text-body-secondary">No matching countries or agents found.</div>
          </div>
        ) : (
          <div className="row g-3 card border shadow rounded-4 mt-2 p-2 pb-3">
            {countriesView.map((country) => {
              const isOpen = expanded.has(country.code);
              return (
                <div key={country.code} className="col-12">
                  <div
                    className="card border shadow-sm rounded-4 balance-card"
                    style={{
                      background:
                        'linear-gradient(180deg, var(--bs-tertiary-bg) 0%, var(--bs-body-bg) 100%)',
                    }}
                  >
                    <div className="card-body ">
                      <div className="d-flex justify-content-between align-items-center flex-wrap">
                        <div className="me-3">
                          <h5 className="card-title d-flex align-items-center mb-1">
                            <img
                              src={`https://flagcdn.com/24x18/${String(country.code || '').toLowerCase()}.png`}
                              alt={country.name}
                              className="me-2 rounded"
                              width={24}
                              height={18}
                              loading="lazy"
                            />
                            {country.name}
                          </h5>
                          <p className="mb-0 text-body-secondary">
                            <span className="ms-2">
                              <strong className="text-body">Total:</strong>{' '}
                              {new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(country.totalAvailableAll)}{' '}
                              {country.currency}
                            </span>
                          </p>
                        </div>

                        <div className="d-flex flex-column align-items-center gap-2 mt-2 mt-md-0">
                          <p className="mb-0 text-body-secondary">
                            <span className="text-body">Agents:</span> {country.agentsCount}
                          </p>
                          <button
                            className="btn btn-outline-secondary btn-sm rounded-pill d-inline-flex align-items-center justify-content-center mt-2"
                            onClick={() => toggleExpand(country.code)}
                            aria-expanded={isOpen}
                          >
                            {isOpen ? <TbChevronUp size={18} /> : <TbChevronDown size={18} />}
                          </button>
                        </div>
                      </div>

                      {isOpen && (
                        <div className="mt-3" id={`agents-${country.code}`}>
                          <AgentBalanceList
                            agents={country.agents || EMPTY}
                            eligibleAgentIds={activeAgentIds}
                            onRefund={isAdmin ? (agent) => openAdjustmentModal('refund', agent) : undefined}
                            onReturn={isAdmin ? (agent) => openAdjustmentModal('return', agent) : undefined}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}
      </div>
      {isAdmin && (
        <BalanceAdjustmentModal
          isOpen={Boolean(adjustmentAction)}
          title={modalTitle}
          subtitle={adjustmentAction ? modalSubtitle : ''}
          submitLabel={modalSubmitLabel}
          agents={adjustableAgents}
          selectedAgent={selectedAdjustmentAgent}
          selectedKey={selectionKey}
          onSelectAgent={handleSelectionChange}
          amount={adjustmentForm.amount}
          reason={adjustmentForm.reason}
          onChange={handleAdjustmentChange}
          onClose={closeAdjustmentModal}
          onSubmit={handleAdjustmentSubmit}
          busy={adjustmentMutation.isPending}
          error={adjustmentError}
        />
      )}
    </>
  );
};

export default Balances;
